/**
 * PreferredTimeCapture · Appraisal Booked · Action 50
 * ────────────────────────────────────────────────────
 * Radio choice for the client's preferred appraisal time bucket, with
 * conditional date + time pickers that reveal only when relevant.
 *
 * Buckets:
 *   anytime       — no specific date/time
 *   date_only     — date picker
 *   date_and_time — date + time pickers
 *
 * Writes on Next:
 *   appraisal.preferred_time_type = <bucket>
 *   appraisal.preferred_date      = "YYYY-MM-DD" | null
 *   appraisal.preferred_time      = "HH:MM"     | null
 * Runner captured_data patch (via write_to):
 *   [write_to]: { type, date, time }
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Pencil, Check, X, Calendar, Clock } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const BUCKETS = [
  { key: 'anytime',       label: 'Anytime',       hint: 'Flexible — we schedule around the appraiser.' },
  { key: 'date_only',     label: 'Specific day',  hint: 'Client wants a specific calendar day.' },
  { key: 'date_and_time', label: 'Day + Time',    hint: 'Client wants a specific day AND a time slot.' },
];

export default function PreferredTimeCapture({
  action, capturedData, value, onChange, onNext, onBack, canBack,
  statusKey, taskId, onTemplateChanged,
}) {
  const params = action.params || {};
  const appraisalId = capturedData?.appraisal_id;

  const seed = (typeof value === 'object' && value) ? value : {};
  const [bucket, setBucket]   = useState(seed.type || '');
  const [date, setDate]       = useState(seed.date || '');
  const [time, setTime]       = useState(seed.time || '');
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState('');
  const [promptDraft, setPromptDraft] = useState(params.prompt || '');
  const [editingPrompt, setEditingPrompt] = useState(false);

  useEffect(() => {
    // reset on action switch
    const s = (typeof value === 'object' && value) ? value : {};
    setBucket(s.type || ''); setDate(s.date || ''); setTime(s.time || '');
  }, [action.id]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPromptDraft(params.prompt || ''); }, [params.prompt, action.id]);

  const canEdit = !!(statusKey && taskId && action?.id);
  const savePrompt = async () => {
    setSaving(true); setErr('');
    try {
      await api.patch(
        `/status-workflows/workflow/${statusKey}/tasks/${taskId}/actions/${action.id}`,
        { params: { prompt: promptDraft.trim() } },
      );
      if (onTemplateChanged) await onTemplateChanged();
      setEditingPrompt(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const dateNeeded = bucket === 'date_only' || bucket === 'date_and_time';
  const timeNeeded = bucket === 'date_and_time';

  const canProceed = !!bucket
    && (!dateNeeded || !!date)
    && (!timeNeeded || !!time);

  const pickBucket = (k) => {
    setBucket(k);
    if (k === 'anytime') { setDate(''); setTime(''); }
    if (k === 'date_only') { setTime(''); }
    const patch = { type: k, date: k === 'anytime' ? '' : date, time: k === 'date_and_time' ? time : '' };
    onChange(patch);
  };

  const setDateVal = (v) => {
    setDate(v);
    onChange({ type: bucket, date: v, time: timeNeeded ? time : '' });
  };
  const setTimeVal = (v) => {
    setTime(v);
    onChange({ type: bucket, date, time: v });
  };

  const commit = async () => {
    if (!canProceed) return;
    setSaving(true); setErr('');
    try {
      if (appraisalId) {
        await api.patch(`/appraisals/${appraisalId}`, {
          preferred_time_type: bucket,
          preferred_date: dateNeeded ? date : null,
          preferred_time: timeNeeded ? time : null,
        });
      }
      const out = { type: bucket, date: dateNeeded ? date : '', time: timeNeeded ? time : '' };
      onChange(out);
      onNext(out);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="wf-preferred-time">
      <div className="bg-gradient-to-br from-sky-50 to-white border-l-4 border-sky-400 rounded-r-lg p-6 mb-6">
        <div className="text-[10px] uppercase tracking-widest text-sky-600 font-semibold mb-2 flex items-center gap-2">
          <span>🗓️ Preferred Time</span>
          {canEdit && !editingPrompt && (
            <button onClick={() => setEditingPrompt(true)} data-testid="wf-time-prompt-edit"
              className="text-sky-500 hover:text-sky-700 flex items-center gap-1">
              <Pencil size={10} /> edit
            </button>
          )}
        </div>
        {editingPrompt ? (
          <div>
            <textarea rows={3} value={promptDraft} onChange={e => setPromptDraft(e.target.value)}
              data-testid="wf-time-prompt-input"
              className="w-full text-base text-slate-800 leading-relaxed border border-sky-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-sky-300" />
            <div className="mt-2 flex items-center gap-2">
              <button onClick={savePrompt} disabled={saving || !promptDraft.trim()}
                className="px-3 py-1 bg-sky-600 text-white text-xs rounded hover:bg-sky-700 disabled:opacity-40 flex items-center gap-1">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
              </button>
              <button onClick={() => { setEditingPrompt(false); setPromptDraft(params.prompt || ''); }}
                className="px-3 py-1 text-slate-500 text-xs hover:text-slate-700 flex items-center gap-1">
                <X size={12} /> Cancel
              </button>
              {err && <span className="text-xs text-rose-600">{err}</span>}
            </div>
          </div>
        ) : (
          <div className="text-lg text-slate-800 leading-relaxed" data-testid="wf-time-prompt">
            {params.prompt || 'When would the client prefer the appraisal?'}
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4" data-testid="wf-time-buckets">
        {BUCKETS.map(b => {
          const active = bucket === b.key;
          return (
            <button key={b.key} type="button" onClick={() => pickBucket(b.key)}
              data-testid={`wf-time-bucket-${b.key}`}
              className={`px-4 py-4 border-2 rounded transition text-left ${active
                ? 'border-sky-500 bg-sky-50 shadow'
                : 'border-slate-200 bg-white hover:border-sky-300 hover:bg-sky-50/40'}`}>
              <div className={`font-semibold ${active ? 'text-sky-700' : 'text-slate-700'}`}>{b.label}</div>
              <div className="text-[11px] text-slate-500 mt-1">{b.hint}</div>
            </button>
          );
        })}
      </div>

      {dateNeeded && (
        <div className="mb-4" data-testid="wf-time-date-row">
          <label className="text-xs font-semibold text-slate-600 flex items-center gap-1 mb-1">
            <Calendar size={12} /> Preferred date
          </label>
          <input type="date" value={date} onChange={e => setDateVal(e.target.value)}
            data-testid="wf-time-date-input"
            className="px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-sky-300" />
        </div>
      )}

      {timeNeeded && (
        <div className="mb-4" data-testid="wf-time-time-row">
          <label className="text-xs font-semibold text-slate-600 flex items-center gap-1 mb-1">
            <Clock size={12} /> Preferred time
          </label>
          <input type="time" value={time} onChange={e => setTimeVal(e.target.value)}
            data-testid="wf-time-time-input"
            className="px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-sky-300" />
        </div>
      )}

      {params.write_to && (
        <div className="text-xs text-slate-400 mb-4">
          <span className="font-semibold text-slate-500">Writes to:</span> <code>{params.write_to}</code> · updates <code>appraisal.preferred_time_type/date/time</code>
        </div>
      )}

      <div className="flex items-center gap-3">
        {canBack && <button onClick={onBack} data-testid="wf-back-btn"
          className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>}
        <span className="flex-1" />
        <button onClick={commit} disabled={!canProceed || saving}
          data-testid="wf-next-btn"
          className="px-6 py-2 bg-sky-600 text-white font-semibold rounded hover:bg-sky-700 disabled:opacity-40 flex items-center gap-2">
          {saving && <Loader2 size={14} className="animate-spin" />} Next →
        </button>
      </div>
    </div>
  );
}
